import React, { useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { empoweringGrowthData } from "../../Components/EmpoweringLogic/EmpoweringLogic";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { motion } from "motion/react";

gsap.registerPlugin(ScrollTrigger);

const EmpoweringGrowth = () => {
  const location = useLocation();
  const sectionRef = useRef(null);
  const cardOneRef = useRef(null);
  const cardTwoRef = useRef(null);

  const {
    title,
    heading,
  } = empoweringGrowthData[location.pathname] || empoweringGrowthData["/"];

  const cards = [
    {
      title: "Your dashboard shows consistent 6-figure months while you're on vacation",
      subtitle: "Complete freedom and passive success",
    },
    {
      title: "Every product launch becomes a guaranteed success story",
      subtitle: "Confidence and predictable wins",
    },
    {
      title: "Industry experts invite you to speak at conferences about your success",
      subtitle: "Fame, recognition, and thought leadership",
    },
    {
      title: "By the time competitors study your strategies, you're already ahead",
      subtitle: "Become the blueprint they follow",
    },
    {
      title: "You’re finally recognized as a true leader in the Amazon space",
      subtitle: "Authority, impact, and legacy",
    },
  ];

  useEffect(() => {
    let ctx = gsap.context(() => {
      const isMobile = window.innerWidth <= 768;
      if (isMobile) return;

      // Animate the text blur/fade as the cards container (.two) enters
      gsap.to(cardOneRef.current, {
        opacity: 0.3,
        filter: "blur(10px)",
        scale: 0.98,
        scrollTrigger: {
          trigger: cardTwoRef.current,
          start: "top 80%",
          end: "top 30%",
          scrub: true,
        },
      });

      // Animate card rows sequentially as they come into view
      const rows = cardTwoRef.current.querySelectorAll(".row-animation");
      rows.forEach((row) => {
        gsap.fromTo(row,
          { y: 150, opacity: 0 },
          {
            y: 0,
            opacity: 1,
            scrollTrigger: {
              trigger: row,
              start: "top 85%", // Animate in when card is well within viewport
              end: "top 40%",
              scrub: true,
            },
          }
        );
      });
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <div ref={sectionRef} className="py-lg-5 text-center empowering-growth">
      {/* Sticky Background Content */}
      <div ref={cardOneRef} className="one">
        <motion.h2
          className="hero-heading text-white"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut", delay: 0.1 }}
          viewport={{ once: true }}
        >
          Imagine Your Amazon Business Running Itself While You Focus on What Matters
        </motion.h2>
        <motion.p
          className="hero-sub-text text-white mt-2 pt-4"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut", delay: 0.2 }}
          viewport={{ once: true }}
        >
          Picture the perfect life after achieving Amazon success
        </motion.p>
      </div>

      {/* Scrolling Cards Foreground */}
      <div ref={cardTwoRef} className="two">
        {/* Row 1 - Cards 1 & 2 */}
        <div className="d-flex justify-content-center flex-wrap gap-5 row-animation">
          {[cards[0], cards[1]].map((card, index) => (
            <div className="card text-white shadow-lg rounded p-4 card-custom" key={`row1-${index}`}>
              <h5 className="card-custom-title">{card.title}</h5>
              <p className="fst-italic mt-2">{card.subtitle}</p>
            </div>
          ))}
        </div>

        {/* Row 2 - Card 3 */}
        <div className="d-flex justify-content-center mt-3 row-animation">
          <div className="card text-white shadow-lg rounded p-4 card-custom">
            <h5 className="card-custom-title">{cards[2].title}</h5>
            <p className="fst-italic mt-2">{cards[2].subtitle}</p>
          </div>
        </div>

        {/* Row 3 - Cards 4 & 5 */}
        <div className="d-flex justify-content-center flex-wrap gap-5 mt-5 row-animation">
          {[cards[3], cards[4]].map((card, index) => (
            <div className="card text-white shadow-lg rounded p-4 card-custom" key={`row3-${index}`}>
              <h5 className="card-custom-title">{card.title}</h5>
              <p className="fst-italic mt-2">{card.subtitle}</p>
            </div>
          ))}
        </div>

        <div className="text-center mt-5">
          <button className="toggle-btn active">
            <Link to={"/signin"} className="text-decoration-none">
              Make This My Reality
            </Link>
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmpoweringGrowth;